import { convertToNumberValue } from "@/utils/convertToNumber";
import { withVariant } from "@/utils/getCombinationString";
import { formatCurrency, formatDecimalString } from "@/utils/formatCurrency";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { QRCodeSVG } from "qrcode.react";
import type {
  FiscalData,
  FiscalIssuer,
  FiscalTransparency,
} from "@/types/printer";
import { escapeHtml } from "@/utils/escapeHtml";

/**
 * Renderiza el QR fiscal del ticket. `qrData` de AFIP es una **URL**
 * (`https://www.afip.gob.ar/fe/qr/?p=...`), así que hay que generar la imagen
 * del QR a partir del payload — no alcanza con un `<img src={url}>`. Sólo cuando
 * ya viene como data-URI lo embebemos directo.
 */
const renderFiscalQr = (qrData: string): string => {
  if (qrData.startsWith("data:")) {
    return `<div class="fiscal-qr"><img src="${qrData}" alt="QR AFIP" /></div>`;
  }
  const svg = renderToStaticMarkup(
    createElement(QRCodeSVG, { value: qrData, size: 160, level: "M" })
  );
  return `<div class="fiscal-qr">${svg}</div>`;
};

/**
 * El emisor (RG 1415, Anexo II, en su orden): razón social, domicilio, CUIT,
 * ingresos brutos, inicio de actividades y condición frente al IVA. Va en el
 * encabezado, como en todo ticket fiscal; lo que el negocio no cargó no tiene
 * renglón.
 */
const renderIssuer = (issuer: FiscalIssuer): string => {
  const rows = [
    issuer.legalName ? `Razón social: ${issuer.legalName}` : "",
    issuer.address ? `Domicilio: ${issuer.address}` : "",
    issuer.cuit ? `CUIT: ${issuer.cuit}` : "",
    issuer.grossIncomeNumber
      ? `Ingresos brutos: ${issuer.grossIncomeNumber}`
      : "",
    issuer.activityStartDate
      ? `Inicio de actividades: ${issuer.activityStartDate}`
      : "",
    issuer.ivaCondition ?? "",
  ].filter(Boolean);
  if (rows.length === 0) return "";
  return `<div class="issuer">${rows
    .map((row) => `<div>${escapeHtml(row)}</div>`)
    .join("")}</div>`;
};

/**
 * El bloque de la Ley 27.743, junto al total. Los montos son strings decimales
 * con punto: van por `formatDecimalString`, nunca por `formatCurrency`, que
 * leería "173.55" como diecisiete mil.
 */
const renderTransparency = (t: FiscalTransparency): string => `
  <div class="transparency">
    <div class="transparency-title">Régimen de Transparencia Fiscal al Consumidor (Ley 27.743)</div>
    <div class="fiscal-row"><span>IVA Contenido</span><span>${escapeHtml(
      formatDecimalString(t.ivaContenido)
    )}</span></div>
    <div class="fiscal-row"><span>Otros Impuestos Nacionales Indirectos</span><span>${escapeHtml(
      formatDecimalString(t.otrosImpuestosNacionalesIndirectos)
    )}</span></div>
  </div>`;

interface TicketProduct {
  quantity: number;
  combinationString: string;
  /** Retiro en otro local: dónde lo tiene que ir a buscar el cliente. */
  pickupWarehouseName?: string;
  /** Y en qué dirección. Vacío si el local no tiene una cargada. */
  pickupWarehouseAddress?: string;
  priceWithPlan?: number | string;
  finalPrice: number | string;
  unitType?: string;
  product?: {
    title: string;
  };
  rel_product?: {
    title: string;
  };
  isLoose?: boolean;
}

interface TicketPaymentMethod {
  checkoutFeeName: string;
  amount: number | string;
}

interface TicketCoupon {
  name?: string;
  code?: string;
}

interface TicketData {
  businessName: string;
  businessPhone?: string;
  businessAddress?: string;
  localName: string;
  localAddress?: string;
  sellerName: string;
  /**
   * ¿Se imprime `sellerName`? Ajuste del negocio
   * (`business_config.showSellerOnDocuments`, ADR-0006 de trd-client — el
   * 0006 de trd-core es otro). Ausente = se imprime,
   * que es el comportamiento de siempre.
   */
  showSeller?: boolean;
  productos: TicketProduct[];
  productosSueltos: TicketProduct[];
  subtotal: number | string;
  discounts?: number | string;
  appliedCoupons?: TicketCoupon[];
  appliedCouponsAmount?: number | string;
  appliedOffersAmount?: number | string;
  total: number | string;
  paymentMethods: TicketPaymentMethod[];
  buyerName?: string;
  /** Si está presente, el ticket se renderiza como comprobante fiscal. */
  fiscalData?: FiscalData;
  /**
   * Retiro en otro local: el código que el cliente dicta en el otro mostrador.
   * Sin esto en el ticket, se va con algo pago y sin nada que reclamar.
   */
  pickupCode?: string | null;
  /**
   * La fecha de la venta, ya formateada (`dd/MM/yyyy HH:mm`). Sin ella, la de
   * ahora: una reimpresión desde la recuperación tiene que decir cuándo fue la
   * venta, no cuándo se reimprimió.
   */
  fecha?: string;
}

/** `K7M4RQ` → `K7M-4RQ`: se guarda sin guión y se muestra con guión. */
const formatPickupCode = (code: string): string =>
  code.length === 6 ? `${code.slice(0, 3)}-${code.slice(3)}` : code;

export const generateTicketHTML = (data: TicketData): string => {
  const formattedDate =
    data.fecha ?? format(new Date(), "dd/MM/yyyy HH:mm", { locale: es });

  const subtotalValue = convertToNumberValue(data.subtotal);
  const discountsValue = convertToNumberValue(data.discounts || 0);
  const couponsAmount = convertToNumberValue(data.appliedCouponsAmount || 0);
  const offersAmount = convertToNumberValue(data.appliedOffersAmount || 0);
  const totalValue = convertToNumberValue(data.total);

  const couponsText =
    data.appliedCoupons && data.appliedCoupons.length > 0
      ? data.appliedCoupons.map((c) => c.code || c.name || "Cupon").join(", ")
      : "";

  const paymentMethodsText = data.paymentMethods
    .map((pm) => pm.checkoutFeeName)
    .join(", ");

  // Build product rows
  const productRows = data.productos
    .map((p) => {
      const name = escapeHtml(
        withVariant(p.product?.title || "Producto", p.combinationString)
      );
      const price = formatCurrency(convertToNumberValue(p.finalPrice));
      return `<tr>
        <td class="qty">${p.quantity}x</td>
        <td class="name">${name}</td>
        <td class="price">${price}</td>
      </tr>`;
    })
    .join("");

  const looseRows = data.productosSueltos
    .map((p) => {
      const name = escapeHtml(
        withVariant(p.rel_product?.title || "Producto", p.combinationString)
      );
      const unit = p.unitType || "";
      const price = formatCurrency(convertToNumberValue(p.finalPrice));
      return `<tr>
        <td class="qty">${p.quantity}${unit}</td>
        <td class="name">${name}</td>
        <td class="price">${price}</td>
      </tr>`;
    })
    .join("");

  // Build discount lines
  let discountLines = "";
  if (discountsValue > 0) {
    discountLines += `<tr>
      <td>Descuentos</td>
      <td class="amount">-${formatCurrency(discountsValue)}</td>
    </tr>`;
  }
  if (offersAmount > 0) {
    discountLines += `<tr>
      <td>Ofertas</td>
      <td class="amount">-${formatCurrency(offersAmount)}</td>
    </tr>`;
  }
  if (couponsAmount > 0) {
    discountLines += `<tr>
      <td>Cupon ${escapeHtml(couponsText)}</td>
      <td class="amount">-${formatCurrency(couponsAmount)}</td>
    </tr>`;
  }

  // Bloque fiscal: sólo cuando la orden está facturada (CAE presente).
  const fd = data.fiscalData;

  // Retiro en otro local: qué NO se lleva puesto y con qué código lo reclama.
  // Va después del total y antes del pie, que es donde el cliente mira cuando
  // le dan el ticket.
  const pickupItems = [
    ...(data.productos ?? []),
    ...(data.productosSueltos ?? []),
  ].filter((item) => !!item.pickupWarehouseName);

  const pickupBlock =
    data.pickupCode && pickupItems.length > 0
      ? `
  <div class="pickup">
    <div class="pickup-title">Retirar en otro local</div>
    <div class="pickup-code">${escapeHtml(
      formatPickupCode(data.pickupCode)
    )}</div>
    ${pickupItems
      .map(
        (item) =>
          `<div class="pickup-line">${item.quantity} x ${escapeHtml(
            item.product?.title ?? item.rel_product?.title ?? "Producto"
          )}</div>
           <div class="pickup-place">${escapeHtml(
             item.pickupWarehouseName ?? ""
           )}</div>` +
          // La dirección va en su propio renglón: pegada al nombre no entra en
          // 32 caracteres y la impresora la corta justo donde importa.
          (item.pickupWarehouseAddress
            ? `<div class="pickup-address">${escapeHtml(
                item.pickupWarehouseAddress
              )}</div>`
            : "")
      )
      .join("")}
    <div class="pickup-line">Presenta este codigo para retirarlo</div>
  </div>`
      : "";
  const issuerBlock = fd?.issuer ? renderIssuer(fd.issuer) : "";
  const transparencyBlock = fd?.transparency
    ? renderTransparency(fd.transparency)
    : "";
  const invoiceTypeLabel = fd?.invoiceType ? fd.invoiceType.trim() : "";
  const qrImg = fd?.qrData ? renderFiscalQr(fd.qrData) : "";
  const fiscalBlock = fd
    ? `
  <div class="fiscal">
    <div class="fiscal-title">COMPROBANTE FISCAL${
      invoiceTypeLabel ? ` &mdash; FACTURA ${escapeHtml(invoiceTypeLabel)}` : ""
    }</div>
    ${
      fd.invoiceNumber
        ? `<div class="fiscal-row"><span>Nro</span><span>${escapeHtml(
            fd.invoiceNumber
          )}</span></div>`
        : ""
    }
    <div class="fiscal-row"><span>CAE</span><span>${escapeHtml(
      fd.cae
    )}</span></div>
    ${
      fd.caeExpiration
        ? `<div class="fiscal-row"><span>Vto CAE</span><span>${escapeHtml(
            fd.caeExpiration
          )}</span></div>`
        : ""
    }
    ${qrImg}
  </div>`
    : "";

  return `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>Ticket de Venta</title>
  <style>
    @page {
      size: 80mm auto;
      margin: 0;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    html {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body {
      width: 80mm;
      max-width: 80mm;
      margin: 0 auto;
      padding: 4mm 3mm;
      font-family: 'Helvetica Neue', Arial, sans-serif;
      font-size: 11px;
      line-height: 1.4;
      background: white;
      color: #000;
    }

    /* Header */
    .header {
      text-align: center;
      padding-bottom: 8px;
      border-bottom: 2px solid #000;
      margin-bottom: 8px;
    }
    .header h1 {
      font-size: 18px;
      font-weight: 800;
      letter-spacing: 1px;
      margin-bottom: 2px;
    }
    .header .address {
      font-size: 9px;
      color: #444;
    }
    .header .issuer {
      font-size: 9px;
      line-height: 1.4;
      margin-top: 4px;
    }

    /* Transparencia fiscal (Ley 27.743) */
    .transparency {
      padding: 4px 0 6px;
      border-bottom: 1px dashed #999;
    }
    .transparency-title {
      font-size: 9px;
      font-weight: 700;
      text-align: center;
      margin-bottom: 2px;
    }

    /* Info */
    .info {
      padding: 6px 0;
      border-bottom: 1px dashed #999;
      margin-bottom: 8px;
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      font-size: 10px;
      line-height: 1.6;
    }
    .info-row .label {
      color: #666;
    }

    /* Products */
    .section-title {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #333;
      margin-bottom: 4px;
    }
    .products {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 8px;
    }
    .products td {
      padding: 3px 0;
      font-size: 10px;
      vertical-align: top;
    }
    .products .qty {
      width: 36px;
      color: #666;
      white-space: nowrap;
    }
    .products .name {
      padding-left: 4px;
      padding-right: 4px;
    }
    .products .price {
      text-align: right;
      white-space: nowrap;
      font-weight: 500;
    }

    /* Totals */
    .totals {
      border-top: 1px dashed #999;
      padding-top: 6px;
      margin-bottom: 4px;
    }
    .totals table {
      width: 100%;
      border-collapse: collapse;
    }
    .totals td {
      padding: 2px 0;
      font-size: 10px;
    }
    .totals .amount {
      text-align: right;
      white-space: nowrap;
    }
    .totals .discount td {
      color: #666;
      font-size: 9px;
    }

    /* Grand total */
    .grand-total {
      border-top: 2px solid #000;
      border-bottom: 2px solid #000;
      padding: 6px 0;
      margin: 6px 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .grand-total .label {
      font-size: 14px;
      font-weight: 800;
    }
    .grand-total .value {
      font-size: 18px;
      font-weight: 800;
    }

    /* Payment */
    .payment {
      text-align: center;
      padding: 6px 0;
      border-bottom: 1px dashed #999;
      font-size: 10px;
      color: #444;
    }
    .payment strong {
      color: #000;
    }

    /* Retiro en otro local */
    .pickup {
      border: 1px dashed #000;
      padding: 6px;
      margin: 8px 0;
      text-align: center;
    }
    .pickup .pickup-title {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
    }
    .pickup .pickup-code {
      font-size: 18px;
      font-weight: 700;
      letter-spacing: 3px;
      margin: 4px 0;
    }
    .pickup .pickup-place {
      font-weight: bold;
      margin-top: 2px;
    }
    .pickup .pickup-address {
      font-size: 11px;
      margin-bottom: 4px;
    }
    .pickup .pickup-line {
      font-size: 9px;
    }

    /* Footer */
    .footer {
      text-align: center;
      padding-top: 8px;
    }
    .footer .thanks {
      font-size: 11px;
      font-weight: 600;
      margin-bottom: 4px;
    }
    .footer .disclaimer {
      font-size: 8px;
      color: #888;
    }

    /* Fiscal */
    .fiscal {
      border-top: 2px solid #000;
      margin-top: 6px;
      padding-top: 6px;
    }
    .fiscal-title {
      text-align: center;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }
    .fiscal-row {
      display: flex;
      justify-content: space-between;
      font-size: 10px;
      line-height: 1.5;
      word-break: break-all;
    }
    .fiscal-qr {
      text-align: center;
      margin-top: 6px;
    }
    .fiscal-qr img,
    .fiscal-qr svg {
      width: 38mm;
      height: 38mm;
      object-fit: contain;
    }

    @media print {
      body {
        width: 80mm;
        padding: 3mm 2mm;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${escapeHtml(data.businessName.toUpperCase())}</h1>
    ${
      data.localAddress
        ? `<div class="address">${escapeHtml(data.localAddress)}</div>`
        : ""
    }
    ${issuerBlock}
  </div>

  <div class="info">
    <div class="info-row">
      <span class="label">Fecha</span>
      <span>${formattedDate}</span>
    </div>
    ${
      data.showSeller === false
        ? ""
        : `<div class="info-row">
            <span class="label">Vendedor</span>
            <span>${escapeHtml(data.sellerName)}</span>
          </div>`
    }
    ${
      data.buyerName
        ? `<div class="info-row">
            <span class="label">Cliente</span>
            <span>${escapeHtml(data.buyerName)}</span>
          </div>`
        : ""
    }
  </div>

  <div class="section-title">Productos</div>
  <table class="products">
    ${productRows}
    ${looseRows}
  </table>

  <div class="totals">
    <table>
      <tr>
        <td>Subtotal</td>
        <td class="amount">${formatCurrency(subtotalValue)}</td>
      </tr>
      ${discountLines}
    </table>
  </div>

  <div class="grand-total">
    <span class="label">TOTAL</span>
    <span class="value">${formatCurrency(totalValue)}</span>
  </div>
  ${transparencyBlock}

  <div class="payment">
    Metodo de pago: <strong>${escapeHtml(paymentMethodsText)}</strong>
  </div>
  ${fiscalBlock}
  ${pickupBlock}

  <div class="footer">
    <div class="thanks">Gracias por su compra!</div>
    ${
      fd ? "" : `<div class="disclaimer">* Comprobante sin validez fiscal</div>`
    }
  </div>
</body>
</html>
  `.trim();
};

export default generateTicketHTML;
